import { change, getFormValues } from "redux-form";
import {
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from "date-fns";
import { useEffect } from "react";
import moment from "moment";

import {
  BULK_ITEM_CATEGORY_OTHER_ID,
  DAY_PERIOD_TYPE_ID,
  DEFAULT_TIME,
  MONTH_PERIOD_TYPE_ID,
  QUARTER_PERIOD_TYPE_ID,
  WEEK_PERIOD_TYPE_ID,
  YEAR_PERIOD_TYPE_ID,
} from "src/constants";
import {
  BulkyItem,
  BulkyItemsCategory,
  BulkyItemSchedulerCitySettings,
  DayOfWeekSetting,
  SchedulerWizardFormValues,
} from "src/interfaces";
import { Column, TextDisplay } from "src/styled";
import { DatePicker, TypedField } from "src/components";
import { DisabledDays } from "src/interfaces/schedulerWizard";
import { getItemSettings } from "src/utils/bulkyItems";
import { isRequired } from "src/utils/validators";
import { ItemSelectionRowWrapper, Wrapper } from "src/styled";
import { setDisabledDays } from "src/redux/reducers";
import { SUNDAY, SUNDAY_FIRST } from "src/constants/weekDays";
import { translate } from "src/services";
import { useAppDispatch } from "src/redux/store";
import { useAppSelector } from "src/redux/store";
import { WIZARD_FORM_NAME } from "./AddressForm";
import { TimeRangeList } from "src/interfaces/citySettings";

interface Props {
  item: BulkyItem;
  category?: BulkyItemsCategory;
  index: number;
}

const ItemPickupDateRow = ({ category, item, index }: Props) => {
  const dispatch = useAppDispatch();

  const addressValidation = useAppSelector((state) => state.bulkyItemsScheduler.addressValidation);
  const pickupsInZone = useAppSelector((state) => state.bulkyItemsScheduler.pickupsInZone);

  const disabledDays = useAppSelector((state) => state.citySettings.disabledDays);
  const citySettings =
    useAppSelector((state) => state.citySettings.settings) ||
    ({} as BulkyItemSchedulerCitySettings);

  const thisFormValues = useAppSelector(
    getFormValues(WIZARD_FORM_NAME)
  ) as SchedulerWizardFormValues;

  const currentDisabledDays = disabledDays[index] || [];

  const currentDisabledDaysFormatted: DisabledDays[] = [];
  currentDisabledDays.forEach((day: DisabledDays) => {
    const dayBefore = day.before ? { before: day.before } : undefined;
    dayBefore && currentDisabledDaysFormatted.push(dayBefore);

    const dayAfter = day.after ? { after: day.after } : undefined;
    dayAfter && currentDisabledDaysFormatted.push(dayAfter);

    const to = day.to ? { to: day.to } : undefined;
    const from = day.from ? { from: day.from } : undefined;

    if (from && to) {
      currentDisabledDaysFormatted.push({
        ...from,
        ...to,
      });
    }

    const date = day.date;
    date && currentDisabledDaysFormatted.push(date);

    const daysOfWeek = day.daysOfWeek;
    daysOfWeek && currentDisabledDaysFormatted.push({ daysOfWeek });
  });

  const newCurrentDisabledDaysFormatted: DisabledDays[] = [];

  const currentDayDisabledBefore = currentDisabledDaysFormatted?.filter((day) => !!day.before);
  !!currentDayDisabledBefore.length &&
    newCurrentDisabledDaysFormatted.push(
      currentDayDisabledBefore[currentDayDisabledBefore.length - 1]
    );

  const currentDayDisabledAfter = currentDisabledDaysFormatted?.filter((day) => !!day.after);
  !!currentDayDisabledAfter.length &&
    newCurrentDisabledDaysFormatted.push(
      currentDayDisabledAfter[currentDayDisabledAfter.length - 1]
    );

  const currentDayDisabledDate = currentDisabledDaysFormatted?.filter(
    (day) => !day.before && !day.after
  );
  !!currentDayDisabledDate.length &&
    newCurrentDisabledDaysFormatted.push(...currentDayDisabledDate);

  const { categoryLabelToDisplay, itemLabelToDisplay, bulkyCategoryTypeId, bulkyItemTypeId, name } =
    item;

  const customMaxFutureSearchableMonths =
    citySettings?.vendorSettings?.maxPickupSchedulePlusMonthsOffset;

  const onDateChange = (date: Date | string) => {
    const itemSettings = getItemSettings(item, date, citySettings.categories, addressValidation);

    const pickupWindowBeginEndTime =
      (itemSettings?.pickupWindowStartTime !== DEFAULT_TIME ||
        itemSettings?.pickupWindowEndTime !== DEFAULT_TIME) &&
      date
        ? `${moment(itemSettings?.pickupWindowStartTime, "HH:mm:ss").format("hh:mm A")} - ${moment(
            itemSettings?.pickupWindowEndTime,
            "HH:mm:ss"
          ).format("hh:mm A")}`
        : null;

    dispatch(
      change(WIZARD_FORM_NAME, `${name}.pickupWindowBeginEndTime`, pickupWindowBeginEndTime)
    );

    const allCalendarItems = Object.keys(thisFormValues.items).map(
      (key) => thisFormValues.items[key]
    );
    const allCalendarItemsFormatted = allCalendarItems
      .map((item) => {
        if (
          item.bulkyCategoryTypeId === bulkyCategoryTypeId &&
          item.bulkyItemTypeId === bulkyItemTypeId
        ) {
          return { ...item, pickupDate: date };
        } else {
          return { ...item };
        }
      })
      .filter((item) => item.pickupDate);

    const countByPickupDate = (
      allCalendarItemsFormatted: BulkyItem[],
      pickupDate: Date | string | any
    ) =>
      allCalendarItemsFormatted.reduce(
        // eslint-disable-next-line
        (prev: any, curr: any) => ((prev[curr[pickupDate]] = ++prev[curr[pickupDate]] || 1), prev),
        {}
      );
    const allCalendarItemsUniq = Object.entries(
      countByPickupDate(allCalendarItemsFormatted, "pickupDate")
    );

    // day of week limitation
    allCalendarItemsUniq.forEach((allCalendarItem: any) => {
      if (category?.dayOfWeekList) {
        const categoriesWithZoneId = category?.dayOfWeekList?.filter(
          (dayOfWeek: DayOfWeekSetting) =>
            addressValidation?.zoneIds?.some((zoneId) => dayOfWeek?.zoneIds?.includes(zoneId))
        );

        const categoriesWithZoneIdFiltered = Object.values(
          categoriesWithZoneId?.reduce((acc, curr) => {
            if (!acc[curr.dayOfWeek] || curr.maxPickups > acc[curr.dayOfWeek].maxPickups) {
              acc[curr.dayOfWeek] = curr;
            }
            return acc;
          }, {} as DayOfWeekSetting as any)
        ) as DayOfWeekSetting[];

        const categoriesWithoutZoneId = category?.dayOfWeekList?.filter(
          (dayOfWeek: DayOfWeekSetting) => !dayOfWeek?.zoneIds?.length
        );

        const currentCategory = !!categoriesWithZoneIdFiltered?.length
          ? categoriesWithZoneIdFiltered
          : categoriesWithoutZoneId;

        const currentDate = allCalendarItem[0];
        const currentPickupsCount = allCalendarItem[1];

        let oldPickupsCount = 0;
        pickupsInZone.forEach((pickupInZone) => {
          if (
            moment(pickupInZone.pickupDate).format("MM/DD/YYYY") ===
            moment(currentDate).format("MM/DD/YYYY")
          ) {
            oldPickupsCount = oldPickupsCount + pickupInZone.currentScheduledPickupCount;
          }
        });

        currentCategory?.forEach((item: DayOfWeekSetting) => {
          const dayOfWeek = item.dayOfWeek === SUNDAY ? SUNDAY_FIRST : item.dayOfWeek;

          if (dayOfWeek === moment(currentDate).day()) {
            if (item.maxPickups <= currentPickupsCount + oldPickupsCount) {
              const newDisabledDays: DisabledDays[] = [];

              disabledDays.map((disabledDay) => {
                newDisabledDays.push([
                  ...disabledDay,
                  { bulkyCategoryTypeId: 0, date: new Date(currentDate) },
                ] as any as DisabledDays);
                return null;
              });

              setDisabledDays(newDisabledDays)(dispatch);
            }
          }
        });
      }
    });

    // time range limitation
    allCalendarItemsUniq.forEach((allCalendarItem: any) => {
      if (category?.timeRangeList) {
        const categoriesWithZoneId = category?.timeRangeList?.filter((timeRange: TimeRangeList) =>
          addressValidation?.zoneIds?.some((zoneId) => timeRange?.zoneIds?.includes(zoneId))
        );

        const categoriesWithZoneIdFiltered = Object.values(
          categoriesWithZoneId?.reduce((acc, curr) => {
            if (
              !acc[curr.periodTypeId as number] ||
              curr.maxPickups > acc[curr.periodTypeId as number].maxPickups
            ) {
              acc[curr.periodTypeId as number] = curr;
            }
            return acc;
          }, {} as TimeRangeList as any)
        ) as TimeRangeList[];

        const categoriesWithoutZoneId = category?.timeRangeList?.filter(
          (timeRange: TimeRangeList) => !timeRange.zoneIds.length
        );

        const currentCategory = !!categoriesWithZoneIdFiltered?.length
          ? categoriesWithZoneIdFiltered
          : categoriesWithoutZoneId;

        const currentDate = allCalendarItem[0];

        let oldPickupsCount = 0;
        pickupsInZone.forEach((pickupInZone) => {
          if (
            moment(pickupInZone.pickupDate).format("MM/DD/YYYY") ===
            moment(currentDate).format("MM/DD/YYYY")
          ) {
            oldPickupsCount = oldPickupsCount + pickupInZone.currentScheduledPickupCount;
          }
        });

        currentCategory?.forEach((item: TimeRangeList) => {
          // time range limitation by day, week, month, quarter, year
          if (
            item.periodTypeId === DAY_PERIOD_TYPE_ID ||
            item.periodTypeId === WEEK_PERIOD_TYPE_ID ||
            item.periodTypeId === MONTH_PERIOD_TYPE_ID ||
            item.periodTypeId === QUARTER_PERIOD_TYPE_ID ||
            item.periodTypeId === YEAR_PERIOD_TYPE_ID
          ) {
            let allCalendarItemsUniqByPeriod: any[] = [];

            allCalendarItemsFormatted.forEach((day) => {
              const pickupDate = new Date(day.pickupDate);
              const start =
                item.periodTypeId === DAY_PERIOD_TYPE_ID
                  ? startOfDay(pickupDate)
                  : item.periodTypeId === WEEK_PERIOD_TYPE_ID
                  ? startOfWeek(pickupDate, { weekStartsOn: 0 })
                  : item.periodTypeId === MONTH_PERIOD_TYPE_ID
                  ? startOfMonth(pickupDate)
                  : item.periodTypeId === QUARTER_PERIOD_TYPE_ID
                  ? startOfQuarter(pickupDate)
                  : startOfYear(pickupDate);
              const end =
                item.periodTypeId === DAY_PERIOD_TYPE_ID
                  ? endOfDay(pickupDate)
                  : item.periodTypeId === WEEK_PERIOD_TYPE_ID
                  ? endOfWeek(pickupDate, { weekStartsOn: 0 })
                  : item.periodTypeId === MONTH_PERIOD_TYPE_ID
                  ? endOfMonth(pickupDate)
                  : item.periodTypeId === QUARTER_PERIOD_TYPE_ID
                  ? endOfQuarter(pickupDate)
                  : endOfYear(pickupDate);
              const currentPeriod = eachDayOfInterval({ start: start, end: end });

              const currentPeriodUniq = allCalendarItemsUniqByPeriod.find(
                (item) => JSON.stringify(item.currentPeriod) === JSON.stringify(currentPeriod)
              );

              let oldPickupsCount = 0;
              pickupsInZone.forEach((pickupInZone) => {
                if (
                  moment(pickupInZone.pickupDate).isBetween(
                    currentPeriod[0],
                    currentPeriod[currentPeriod.length - 1]
                  )
                ) {
                  oldPickupsCount = oldPickupsCount + pickupInZone.currentScheduledPickupCount;
                }
              });

              if (!!currentPeriodUniq) {
                const newAllCalendarItemsUniqByPeriod = allCalendarItemsUniqByPeriod.map((item) => {
                  return {
                    ...item,
                    occurencies:
                      JSON.stringify(item.currentPeriod) === JSON.stringify(currentPeriod)
                        ? item.occurencies + 1
                        : item.occurencies,
                  };
                });
                allCalendarItemsUniqByPeriod = newAllCalendarItemsUniqByPeriod;
              } else {
                allCalendarItemsUniqByPeriod.push({
                  pickupDate: day.pickupDate,
                  occurencies: 1,
                  currentPeriod,
                });
              }

              allCalendarItemsUniqByPeriod.forEach((itemByPeriod) => {
                if (itemByPeriod.occurencies + oldPickupsCount >= item.maxPickups) {
                  const newDisabledDays: DisabledDays[] = [];

                  disabledDays.map((disabledDay) => {
                    newDisabledDays.push([
                      ...disabledDay,
                      {
                        bulkyCategoryTypeId: 0,
                        to: itemByPeriod.currentPeriod[itemByPeriod.currentPeriod.length - 1],
                        from: itemByPeriod.currentPeriod[0],
                      },
                    ] as any as DisabledDays);
                    return null;
                  });

                  setDisabledDays(newDisabledDays)(dispatch);
                }
              });
            });
          }
        });
      }
    });

    // location limitation
    allCalendarItemsUniq.forEach((allCalendarItem: any) => {
      const currentDate = allCalendarItem[0];
      const currentPickupsCount = allCalendarItem[1];

      const serviceLocationThresholdSettings =
        citySettings.vendorSettings.serviceLocationThresholdSettings[0];
      const maxLocationPickups = serviceLocationThresholdSettings?.maxLocationPickups;

      // location limitation by day
      if (serviceLocationThresholdSettings?.periodTypeId === DAY_PERIOD_TYPE_ID) {
        let oldPickupsCount = 0;

        addressValidation.scheduledPickupsSummary?.scheduledPickups?.forEach((scheduledPickup) => {
          if (
            moment(scheduledPickup.pickupDate).format("MM/DD/YYYY") ===
            moment(currentDate).format("MM/DD/YYYY")
          ) {
            oldPickupsCount = oldPickupsCount + scheduledPickup.totalPickups;
          }
        });

        if (maxLocationPickups <= currentPickupsCount + oldPickupsCount) {
          const newDisabledDays: DisabledDays[] = [];

          disabledDays.map((disabledDay) => {
            newDisabledDays.push([
              ...disabledDay,
              { bulkyCategoryTypeId: 0, date: new Date(currentDate) },
            ] as any as DisabledDays);
            return null;
          });

          setDisabledDays(newDisabledDays)(dispatch);
        }
      }

      // location limitation by week, month, quarter, year
      if (
        serviceLocationThresholdSettings?.periodTypeId === WEEK_PERIOD_TYPE_ID ||
        serviceLocationThresholdSettings?.periodTypeId === MONTH_PERIOD_TYPE_ID ||
        serviceLocationThresholdSettings?.periodTypeId === QUARTER_PERIOD_TYPE_ID ||
        serviceLocationThresholdSettings?.periodTypeId === YEAR_PERIOD_TYPE_ID
      ) {
        let allCalendarItemsUniqByPeriod: any[] = [];

        allCalendarItemsFormatted.forEach((day) => {
          const pickupDate = new Date(day.pickupDate);
          const start =
            serviceLocationThresholdSettings?.periodTypeId === WEEK_PERIOD_TYPE_ID
              ? startOfWeek(pickupDate, { weekStartsOn: 0 })
              : serviceLocationThresholdSettings?.periodTypeId === MONTH_PERIOD_TYPE_ID
              ? startOfMonth(pickupDate)
              : serviceLocationThresholdSettings?.periodTypeId === QUARTER_PERIOD_TYPE_ID
              ? startOfQuarter(pickupDate)
              : startOfYear(pickupDate);
          const end =
            serviceLocationThresholdSettings?.periodTypeId === WEEK_PERIOD_TYPE_ID
              ? endOfWeek(pickupDate, { weekStartsOn: 0 })
              : serviceLocationThresholdSettings?.periodTypeId === MONTH_PERIOD_TYPE_ID
              ? endOfMonth(pickupDate)
              : serviceLocationThresholdSettings?.periodTypeId === QUARTER_PERIOD_TYPE_ID
              ? endOfQuarter(pickupDate)
              : endOfYear(pickupDate);
          const currentPeriod = eachDayOfInterval({ start: start, end: end });

          const currentPeriodUniq = allCalendarItemsUniqByPeriod.find(
            (item) => JSON.stringify(item.currentPeriod) === JSON.stringify(currentPeriod)
          );

          let oldPickupsCount = 0;
          addressValidation.scheduledPickupsSummary?.scheduledPickups?.forEach(
            (scheduledPickup) => {
              if (
                moment(scheduledPickup.pickupDate).isBetween(
                  currentPeriod[0],
                  currentPeriod[currentPeriod.length - 1]
                )
              ) {
                oldPickupsCount = oldPickupsCount + scheduledPickup.totalPickups;
              }
            }
          );

          if (!!currentPeriodUniq) {
            const newAllCalendarItemsUniqByPeriod = allCalendarItemsUniqByPeriod.map((item) => {
              return {
                ...item,
                occurencies:
                  JSON.stringify(item.currentPeriod) === JSON.stringify(currentPeriod)
                    ? item.occurencies + 1
                    : item.occurencies,
              };
            });
            allCalendarItemsUniqByPeriod = newAllCalendarItemsUniqByPeriod;
          } else {
            allCalendarItemsUniqByPeriod.push({
              pickupDate: day.pickupDate,
              occurencies: 1,
              currentPeriod,
            });
          }

          allCalendarItemsUniqByPeriod.forEach((itemByPeriod) => {
            if (itemByPeriod.occurencies + oldPickupsCount >= maxLocationPickups) {
              const newDisabledDays: DisabledDays[] = [];

              disabledDays.map((disabledDay) => {
                newDisabledDays.push([
                  ...disabledDay,
                  {
                    bulkyCategoryTypeId: 0,
                    to: itemByPeriod.currentPeriod[itemByPeriod.currentPeriod.length - 1],
                    from: itemByPeriod.currentPeriod[0],
                  },
                ] as any as DisabledDays);
                return null;
              });

              setDisabledDays(newDisabledDays)(dispatch);
            }
          });
        });
      }
    });
  };

  useEffect(() => {
    dispatch(change(WIZARD_FORM_NAME, `${name}.pickupDate`, null));
  }, [item.pickupDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // before today every day is disabled
  const beforeToday = { before: moment().toDate() };

  // set all disabled days
  const allDisabledDays = [...newCurrentDisabledDaysFormatted, beforeToday];

  return (
    <>
      <ItemSelectionRowWrapper>
        <Column lg={6} padding="no">
          <TextDisplay size="xxSmall" color="grayDarker" block margin="no no xxSmall">
            {categoryLabelToDisplay}
          </TextDisplay>
          {bulkyItemTypeId && (
            <TextDisplay size="small" block>
              {itemLabelToDisplay}
            </TextDisplay>
          )}
          {bulkyCategoryTypeId === BULK_ITEM_CATEGORY_OTHER_ID && !bulkyItemTypeId && (
            <TextDisplay size="small" block>
              {item.customBulkyItemName}
            </TextDisplay>
          )}
        </Column>
        {/*TODO: nice to have in the future would be an "i" icon that on hover displays info popup with the
         reason for the day being disabled in the calendar */}
        <Column lg={6} padding="no">
          <Wrapper display="flex" justifyContent="flex-end">
            <TypedField
              component={DatePicker}
              name={`${name}.pickupDate`}
              props={{
                margin: "no",
                placeholder: translate("wizardPage.pickupDatePlaceholder"),
                disabledDays: allDisabledDays,
                color: "primary",
                noBottomBorder: true,
                width: "170px",
                customMaxFutureSearchableMonths: customMaxFutureSearchableMonths,
                onDateChange: onDateChange,
              }}
              validate={[isRequired]}
            />
          </Wrapper>
        </Column>
      </ItemSelectionRowWrapper>
    </>
  );
};

export default ItemPickupDateRow;
